import React, { useState, useEffect } from "react";
import { gardenService } from "../services/gardenService";
import api from "../services/api";

// API base URL (same as api.js - for image URL normalization)
const getApiBaseUrl = () => {
  const url = (process.env.REACT_APP_API_URL || "").trim();
  if (url) return url.replace(/\/$/, "");
  return process.env.NODE_ENV === "development"
    ? "http://localhost:4001/api"
    : "https://newgardenbackend.amcfest.in/api";
};

// Local placeholder image (SVG data URI)
const placeholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='96' height='96'%3E%3Crect width='96' height='96' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='10' fill='%236b7280'%3ENo Image%3C/text%3E%3C/svg%3E";
const placeholderImageLarge = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='300' height='200' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='%236b7280'%3EImage Not Found%3C/text%3E%3C/svg%3E";

// Normalize image URL for proper display
const normalizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return null;

  let finalUrl = url.trim();
  // Fix double /api/api/ in stored URLs (backend may have saved wrong base)
  finalUrl = finalUrl.replace(/\/api\/api\//g, "/api/");

  // Replace localhost URLs with backend origin (for image links)
  const apiBaseUrl = getApiBaseUrl();
  const backendOrigin = apiBaseUrl.replace(/\/api\/?$/, "");
  if (finalUrl.includes("localhost:4001")) {
    finalUrl = finalUrl.replace(/http:\/\/localhost:4001/g, backendOrigin);
  }

  const API_BASE_URL = apiBaseUrl;

  // If already a full URL, return as is (handles https://..../api/Images/...)
  if (finalUrl.startsWith("http://") || finalUrl.startsWith("https://")) {
    return finalUrl;
  }

  // Handle paths starting with /api/Images/ (relative to base)
  if (finalUrl.startsWith("/api/Images/")) {
    const apiBaseWithoutApi = API_BASE_URL.replace("/api", "").replace(
      /\/$/,
      ""
    );
    return `${apiBaseWithoutApi}${finalUrl}`;
  }

  // Handle relative paths (starting with /)
  if (finalUrl.startsWith("/")) {
    const apiBaseWithoutApi = API_BASE_URL.replace("/api", "").replace(
      /\/$/,
      ""
    );
    return `${apiBaseWithoutApi}${finalUrl}`;
  }

  // Handle paths without protocol
  if (!finalUrl.startsWith("http://") && !finalUrl.startsWith("https://")) {
    if (finalUrl.startsWith("//")) {
      finalUrl = `http:${finalUrl}`;
    } else {
      const apiBaseWithoutApi = API_BASE_URL.replace("/api", "").replace(
        /\/$/,
        ""
      );
      return `${apiBaseWithoutApi}${
        finalUrl.startsWith("/") ? "" : "/"
      }${finalUrl}`;
    }
  }

  return finalUrl;
};

// Get all garden image URLs (array) - from images JSON or single image
const getGardenImages = (garden) => {
  if (!garden) return [];
  if (garden.images) {
    try {
      const parsed = typeof garden.images === "string" ? JSON.parse(garden.images) : garden.images;
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((u) => (u ? normalizeImageUrl(String(u)) : null)).filter(Boolean);
      }
    } catch (_) {}
  }
  const single =
    garden.image ||
    garden.image_url ||
    garden.imageUrl ||
    garden.photo ||
    garden.photo_url;
  return single ? [normalizeImageUrl(single)] : [];
};

// First image for thumbnail
const getGardenImage = (garden) => {
  const arr = getGardenImages(garden);
  return arr.length > 0 ? arr[0] : null;
};

const GardenManagement = () => {
  const [gardens, setGardens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingGarden, setEditingGarden] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [viewingGarden, setViewingGarden] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const [formData, setFormData] = useState({
    name: "",
    area: "",
    type: "",
    division: "",
    wardno: "",
    adress: "",
    facilities: "",
    uniqueness: "",
    opening_time: "",
    clossing_time: "",
    location: "",
    garden_maintain: "",
    image: "",
  });

  useEffect(() => {
    loadGardens();
    loadDivisions();
  }, []);

  const loadGardens = async () => {
    setLoading(true);
    try {
      const response = await gardenService.getAllGardens();
      const list = response?.data?.data ?? response?.data;
      setGardens(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error loading gardens:", error);
      const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message;
      alert("Error loading gardens: " + (msg || "Network error"));
      setGardens([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDivisions = async () => {
    try {
      const response = await api.get("/divisions");
      const list = response?.data?.data ?? response?.data;
      setDivisions(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error loading divisions:", error);
      setDivisions([]);
    }
  };

  const handleImageChange = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;
    const valid = files.filter((f) => f.type.startsWith("image/"));
    setImageFiles((prev) => [...prev, ...valid]);
    valid.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeNewImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCreateOrUpdateGarden = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert("Garden name is required!");
      return;
    }
    setSaving(true);
    try {
      const submitData = { ...formData };
      submitData.images = imageFiles;
      if (editingGarden) {
        submitData.existingImages = JSON.stringify(existingImages);
      }
      if (editingGarden) {
        await gardenService.updateGarden(editingGarden.id, submitData);
        alert("Garden updated successfully!");
      } else {
        await gardenService.createGarden(submitData);
        alert("Garden created successfully!");
      }
      setFormData({
        name: "",
        area: "",
        type: "",
        division: "",
        wardno: "",
        adress: "",
        facilities: "",
        uniqueness: "",
        opening_time: "",
        clossing_time: "",
        location: "",
        garden_maintain: "",
        image: "",
      });
      setImageFiles([]);
      setImagePreviews([]);
      setExistingImages([]);
      setShowForm(false);
      setEditingGarden(null);
      loadGardens();
    } catch (error) {
      console.error("Error saving garden:", error);
      const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message;
      alert(msg || "Error saving garden");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (garden) => {
    setEditingGarden(garden);
    setExistingImages(getGardenImages(garden));
    setImageFiles([]);
    setImagePreviews([]);
    setFormData({
      name: garden.name || "",
      area: garden.area || "",
      type: garden.type || "",
      division: garden.division || "",
      wardno: garden.wardno || "",
      adress: garden.adress || garden.address || "",
      facilities: garden.facilities || "",
      uniqueness: garden.uniqueness || "",
      opening_time: garden.opening_time || "",
      clossing_time: garden.clossing_time || "",
      location: garden.location || "",
      garden_maintain: garden.garden_maintain || "",
      image: garden.image || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this garden?")) {
      return;
    }
    setLoading(true);
    try {
      await gardenService.deleteGarden(id);
      alert("Garden deleted successfully!");
      loadGardens();
    } catch (error) {
      console.error("Error deleting garden:", error);
      const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message;
      alert(msg || "Error deleting garden");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setCurrentPage(1);
    if (!searchQuery.trim()) {
      loadGardens();
      return;
    }
    setLoading(true);
    try {
      const response = await gardenService.searchGardens(searchQuery);
      const list = response?.data?.data ?? response?.data;
      setGardens(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error searching gardens:", error);
      const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message;
      alert("Error searching gardens: " + (msg || "Network error"));
    } finally {
      setLoading(false);
    }
  };

  const filteredGardens = gardens.filter((garden) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      (garden.name && garden.name.toLowerCase().includes(query)) ||
      (garden.division && garden.division.toLowerCase().includes(query)) ||
      (garden.area && garden.area.toLowerCase().includes(query)) ||
      (garden.adress && garden.adress.toLowerCase().includes(query)) ||
      (garden.address && garden.address.toLowerCase().includes(query))
    );
  });

  const totalRecords = filteredGardens.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedGardens = filteredGardens.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    if (totalPages >= 1 && currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const goToPage = (page) => {
    const p = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(p);
  };

  if (loading && gardens.length === 0) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Garden Management</h2>
          <button
            onClick={() => {
              setShowForm(!showForm);
              setEditingGarden(null);
              setImageFiles([]);
              setImagePreviews([]);
              setExistingImages([]);
              setFormData({
                name: "",
                area: "",
                type: "",
                division: "",
                wardno: "",
                adress: "",
                facilities: "",
                uniqueness: "",
                opening_time: "",
                clossing_time: "",
                location: "",
                garden_maintain: "",
                image: "",
              });
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            {showForm ? "Cancel" : "+ Create Garden"}
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6 flex gap-2">
          <input
            type="text"
            placeholder="Search by name, division, area, or address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSearch}
            className="bg-orange-400 text-white px-6 py-2 rounded-md hover:bg-orange-700"
          >
            Search
          </button>
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery("");
                setCurrentPage(1);
                loadGardens();
              }}
              className="bg-gray-400 text-white px-4 py-2 rounded-md hover:bg-gray-500"
            >
              Clear
            </button>
          )}
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4">
                {editingGarden ? "Edit Garden" : "Create New Garden"}
              </h3>
              <form onSubmit={handleCreateOrUpdateGarden} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Garden Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter garden name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Area
                    </label>
                    <input
                      type="text"
                      value={formData.area}
                      onChange={(e) =>
                        setFormData({ ...formData, area: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter area"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type
                    </label>
                    <input
                      type="text"
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({ ...formData, type: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Public Park, Botanical Garden"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Division *
                    </label>
                    <select
                      value={formData.division}
                      onChange={(e) =>
                        setFormData({ ...formData, division: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Division</option>
                      {divisions.map((division) => (
                        <option
                          key={division.id}
                          value={division.division_name}
                        >
                          {division.division_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ward Number
                    </label>
                    <input
                      type="text"
                      value={formData.wardno}
                      onChange={(e) =>
                        setFormData({ ...formData, wardno: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter ward number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <input
                      type="text"
                      value={formData.adress}
                      onChange={(e) =>
                        setFormData({ ...formData, adress: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter complete address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Opening Time
                    </label>
                    <input
                      type="time"
                      value={formData.opening_time}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          opening_time: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Closing Time
                    </label>
                    <input
                      type="time"
                      value={formData.clossing_time}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          clossing_time: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) =>
                        setFormData({ ...formData, location: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter location details"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Garden Maintain
                    </label>
                    <input
                      type="text"
                      value={formData.garden_maintain}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          garden_maintain: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter maintenance details"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Garden Images (multiple)
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                    {(existingImages.length > 0 || imagePreviews.length > 0) && (
                      <div className="mt-4 flex flex-wrap gap-3">
                        {existingImages.map((url, index) => (
                          <div key={`ex-${index}`} className="relative">
                            <img
                              src={url}
                              alt={`Existing ${index + 1}`}
                              className="w-24 h-24 object-cover rounded-md border border-gray-300"
                              onError={(e) => {
                                e.target.src = placeholderImageLarge;
                                e.target.onerror = null;
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => removeExistingImage(index)}
                              className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center hover:bg-red-600"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        {imagePreviews.map((src, index) => (
                          <div key={`new-${index}`} className="relative">
                            <img
                              src={src}
                              alt={`New ${index + 1}`}
                              className="w-24 h-24 object-cover rounded-md border border-gray-300"
                              onError={(e) => {
                                e.target.src = placeholderImageLarge;
                                e.target.onerror = null;
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => removeNewImage(index)}
                              className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center hover:bg-red-600"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Facilities
                  </label>
                  <textarea
                    value={formData.facilities}
                    onChange={(e) =>
                      setFormData({ ...formData, facilities: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Enter facilities available (e.g., Playground, Walking track, Benches)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Uniqueness
                  </label>
                  <textarea
                    value={formData.uniqueness}
                    onChange={(e) =>
                      setFormData({ ...formData, uniqueness: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Enter unique features of this garden"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving
                      ? "Saving..."
                      : editingGarden
                      ? "Update Garden"
                      : "Create Garden"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingGarden(null);
                    }}
                    className="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View Garden Modal */}
        {viewingGarden && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-800">Garden Details</h3>
                <button
                  type="button"
                  onClick={() => setViewingGarden(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                >
                  &times;
                </button>
              </div>
              <div className="p-6 space-y-4">
                {getGardenImages(viewingGarden).length > 0 && (
                  <div className="flex flex-wrap gap-2 justify-center">
                    {getGardenImages(viewingGarden).map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt={`${viewingGarden.name} ${i + 1}`}
                        className="max-h-48 w-auto rounded-lg border object-contain cursor-pointer hover:opacity-90"
                        onClick={() => window.open(url, "_blank")}
                        onError={(e) => { e.target.src = placeholderImageLarge; e.target.onerror = null; }}
                      />
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><span className="text-gray-500 text-sm">Name</span><p className="font-medium">{viewingGarden.name || "—"}</p></div>
                  <div><span className="text-gray-500 text-sm">Division</span><p className="font-medium">{viewingGarden.division || "—"}</p></div>
                  <div><span className="text-gray-500 text-sm">Area</span><p className="font-medium">{viewingGarden.area || "—"}</p></div>
                  <div><span className="text-gray-500 text-sm">Type</span><p className="font-medium">{viewingGarden.type || "—"}</p></div>
                  <div><span className="text-gray-500 text-sm">Ward No</span><p className="font-medium">{viewingGarden.wardno || "—"}</p></div>
                  <div><span className="text-gray-500 text-sm">Opening / Closing</span><p className="font-medium">{viewingGarden.opening_time || "—"} / {viewingGarden.clossing_time || "—"}</p></div>
                  <div className="sm:col-span-2"><span className="text-gray-500 text-sm">Address</span><p className="font-medium">{viewingGarden.adress || viewingGarden.address || "—"}</p></div>
                  <div className="sm:col-span-2"><span className="text-gray-500 text-sm">Location</span><p className="font-medium">{viewingGarden.location || "—"}</p></div>
                  <div className="sm:col-span-2"><span className="text-gray-500 text-sm">Facilities</span><p className="font-medium whitespace-pre-wrap">{viewingGarden.facilities || "—"}</p></div>
                  <div className="sm:col-span-2"><span className="text-gray-500 text-sm">Uniqueness</span><p className="font-medium whitespace-pre-wrap">{viewingGarden.uniqueness || "—"}</p></div>
                  <div className="sm:col-span-2"><span className="text-gray-500 text-sm">Garden Maintain</span><p className="font-medium">{viewingGarden.garden_maintain || "—"}</p></div>
                </div>
                <div className="flex gap-2 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => { setViewingGarden(null); handleEdit(viewingGarden); }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewingGarden(null)}
                    className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <div className="p-4 border-b flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-gray-600">
              Total: <span className="font-semibold">{totalRecords}</span> gardens
              {totalRecords > 0 && (
                <span className="ml-2 text-gray-500">
                  (Showing {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalRecords)})
                </span>
              )}
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="px-3 py-1.5 rounded border border-gray-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1.5 rounded border border-gray-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
          <table className="min-w-full border-collapse border border-gray-300">
            <thead className="bg-gradient-to-r from-teal-600 to-emerald-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                  Image
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300 whitespace-normal">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                  Division
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                  Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border border-gray-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {filteredGardens.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    className="px-6 py-8 text-center text-sm text-gray-500 border border-gray-300"
                  >
                    {searchQuery
                      ? "No gardens found matching your search."
                      : "No gardens found. Click '+ Create Garden' to add a new garden."}
                  </td>
                </tr>
              ) : (
                paginatedGardens.map((garden) => (
                  <tr key={garden.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border border-gray-300">
                      {garden.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap border border-gray-300">
                      {(() => {
                        const imageUrl = getGardenImage(garden);
                        if (imageUrl) {
                          return (
                            <div className="relative">
                              <img
                                src={imageUrl}
                                alt={garden.name || "Garden"}
                                className="w-24 h-24 object-cover rounded-md border-2 border-gray-300 shadow-md cursor-pointer hover:shadow-lg hover:scale-105 transition-all"
                                onError={(e) => {
                                  // Silently handle error - use local placeholder
                                  e.target.src = placeholderImage;
                                  e.target.onclick = null; // Disable click on placeholder
                                  e.target.onerror = null; // Prevent infinite loop
                                }}
                                onClick={() => {
                                  window.open(imageUrl, "_blank");
                                }}
                                title="Click to view full size"
                              />
                            </div>
                          );
                        } else {
                          return (
                            <div className="w-24 h-24 bg-gray-200 rounded-md border-2 border-gray-300 flex items-center justify-center text-xs text-gray-500">
                              No Image
                            </div>
                          );
                        }
                      })()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 border border-gray-300 whitespace-normal break-words max-w-[200px]">
                      {garden.name || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border border-gray-300">
                      {garden.division || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate border border-gray-300">
                      {garden.adress || garden.address || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium border border-gray-300">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setViewingGarden(garden)}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50 transition-colors"
                          title="View Garden"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleEdit(garden)}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50 transition-colors"
                          title="Edit Garden"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(garden.id)}
                          className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50 transition-colors"
                          title="Delete Garden"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {totalRecords > 0 && totalPages > 1 && (
            <div className="p-4 border-t flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-gray-500">
                Showing {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}
              </p>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="px-3 py-1.5 rounded border border-gray-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1.5 rounded border border-gray-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GardenManagement;
